import axiosInstance from '../utils/axios';

/**
 * 혈당수치 관리 API 서비스
 */
const glucoService = {
    /**
     * 혈당 데이터 목록 조회
     * @param {Object} params - 조회 파라미터
     * @param {number} params.page - 페이지 번호
     * @param {number} params.pageSize - 페이지 크기
     * @param {number} params.searchKind - 검색 종류 (0: 전체, 1: 회원명, 2: 아이디, 3: 이메일)
     * @param {string} params.keyword - 검색 키워드
     * @returns {Promise<Object>} 혈당 데이터 목록
     */
    async getGlucoList({ page = 1, pageSize = 10, searchKind = 0, keyword = '' } = {}) {
        const response = await axiosInstance.get('/gluco/list', {
            params: {
                page,
                pageSize,
                searchKind,
                keyword
            }
        });
        return response.data;
    },

    /**
     * 혈당 데이터 상세 조회
     * @param {number} id - 혈당 데이터 ID
     * @returns {Promise<Object>} 혈당 데이터 상세 정보
     */
    async getGlucoDetail(id) {
        const response = await axiosInstance.get(`/gluco/${id}`);
        return response.data;
    },

    /**
     * 혈당 데이터 검색 목록 (미리보기용)
     * @param {string} keyword - 검색 키워드
     * @param {number} searchKind - 검색 종류 (0:전체, 1:회원명, 2:아이디, 3:이메일)
     * @returns {Promise<Array>} 사용자 정보 목록
     */
    async getSearchSuggest(keyword, searchKind = 0) {
        try{
            const response = await axiosInstance.get('/gluco/search-suggest', {
                params: {
                    searchKind,
                    keyword
                }
            });
            if (response.data.success) {
                return response.data.data;
            } else {
                console.error('User search failed:', response.data.message);
                return [];
            }
        } catch (error) {
            console.error('Error searching users for auto-complete:', error);
            return [];
        }
    },

    /**
     * 혈당 데이터 엑셀 다운로드
     * @param {Object} params - 다운로드 파라미터
     * @param {number} params.searchKind - 검색 종류
     * @param {string} params.keyword - 검색 키워드
     * @returns {Promise<Object>} 엑셀 다운로드 데이터
     */
    async downloadGlucoExcel({ searchKind = 0, keyword = '' } = {}) {
        const response = await axiosInstance.get('/gluco/excel', {
            params: {
                searchKind,
                keyword
            }
        });
        return response.data;
    },

    /**
     * 혈당 데이터 생성
     * @param {Object} glucoData - 혈당 데이터
     * @returns {Promise<Object>} 생성 결과
     */
    async createGluco(glucoData) {
        const response = await axiosInstance.post('/gluco', glucoData);
        return response.data;
    },

    /**
     * 혈당 데이터 수정
     * @param {number} id - 혈당 데이터 ID
     * @param {Object} glucoData - 수정할 혈당 데이터
     * @returns {Promise<Object>} 수정 결과
     */
    async updateGluco(id, glucoData) {
        const response = await axiosInstance.put(`/gluco/${id}`, glucoData);
        return response.data;
    },

    /**
     * 혈당 데이터 삭제
     * @param {number} id - 혈당 데이터 ID
     * @returns {Promise<Object>} 삭제 결과
     */
    async deleteGluco(id) {
        const response = await axiosInstance.delete(`/gluco/${id}`);
        return response.data;
    },

    /**
     * 사용자별 혈당 데이터 삭제
     * @param {number} userIdx - 사용자 ID
     * @returns {Promise<Object>} 삭제 결과
     */
    async deleteGlucoByUser(userIdx) {
        const response = await axiosInstance.delete(`/gluco/user/${userIdx}`);
        return response.data;
    },

    /**
     * 혈당 데이터 일괄 삭제
     * @param {Array<number>} glucoIds - 삭제할 혈당 데이터 ID 목록
     * @returns {Promise<Object>} 삭제 결과
     */
    async deleteGlucoList(glucoIds) {
        const response = await axiosInstance.delete('/gluco/bulk', {
            data: { glucoIds }
        });
        return response.data;
    },

    /**
     * 특정 사용자의 혈당 데이터 목록 조회
     * @param {number} userIdx - 사용자 ID
     * @param {Object} params - 조회 파라미터
     * @param {number} params.page - 페이지 번호
     * @param {number} params.pageSize - 페이지 크기
     * @param {string} params.state - 상태 필터
     * @param {string} params.year - 연도 필터
     * @param {string} params.month - 월 필터
     * @returns {Promise<Object>} 사용자 혈당 데이터 목록
     */
    async getUserGlucoList(userIdx, {state = null, year = null, month = null } = {}) {
        const params = {};
        if (state) params.state = state;
        if (year) params.year = year;
        if (month) params.month = month;

        const response = await axiosInstance.get(`/gluco/user/${userIdx}`, { params });
        return response.data;
    },

    /**
     * 사용자별 혈당 데이터 엑셀 다운로드
     * @param {number} userIdx - 사용자 ID
     * @param {Object} params - 다운로드 파라미터
     * @param {string} params.state - 상태 필터
     * @param {string} params.year - 연도 필터
     * @param {string} params.month - 월 필터
     * @returns {Promise<Object>} 엑셀 다운로드 데이터
     */
    async downloadUserGlucoExcel(userIdx, { state = null, year = null, month = null } = {}) {
        const params = {};
        if (state) params.state = state;
        if (year) params.year = year;
        if (month) params.month = month;

        const response = await axiosInstance.get(`/gluco/user/${userIdx}/excel`, { params });
        return response.data;
    },

    /**
     * 사용자 혈당 데이터 보고서 조회
     * @param {number} userIdx - 사용자 ID
     * @param {number} day - 기간 (일 단위)
     * @returns {Promise<Object>} 보고서 데이터
     */
    async getUserGlucoReport(userIdx, day = 7) {
        const params = { day };
        const response = await axiosInstance.get(`/gluco/user/${userIdx}/report`, { params });
        return response.data;
    }
};

export default glucoService;
